import { statesByAbbrev, territoriesByAbbrev } from '@scoir/states'

import { Class, HistoricFollowerYear } from './ExploreTable/gradYears'

export type ExploreHighSchool = {
  id: string
  target?: boolean
  name: string
  city: string
  state: string
  zipCode?: string
  schoolType?: SchoolType
  seniorClassSize?: number
  nextVisitDate?: string
  title1Program?: Title1Program
  pctFreeReducedLunch?: number
  pctFirstGenerationStudents?: number
  pctAttendFourYearCollegeStudents?: number
  pctAttendTwoYearCollegeStudents?: number
  pctWhiteCaucasianStudents?: number
  pctBlackAfricanAmericanStudents?: number
  pctHispanicLatinoStudents?: number
  pctAsianStudents?: number
  pctNativeAmericanStudents?: number
  pctOtherUndisclosedStudents?: number
  isActive: boolean
  ceebCode?: string
  isTitle1Eligible?: boolean
  religiousAffiliation?: string
  locale?: LocaleOption
  geomarket?: string
  numHandraisersByGradYear?: Record<string, number>
  numFollowersByGradYear?: Record<string, number>
  numHistoricFollowersByGradYear?: Record<string, number>
  hasCounselorSuggestions?: boolean
}

export type SchoolType = 'public' | 'private'
export type Title1Program = 'swp' | 'tas'

type Territories = keyof typeof territoriesByAbbrev
type States = keyof typeof statesByAbbrev
export const unknownLocation = '' as const
type UnknownLocation = typeof unknownLocation
export type HSLocation = Territories | States | UnknownLocation

export const LOCALE_OPTIONS = [
  'Rural: Distant',
  'City: Large',
  'Suburb: Large',
  'Rural: Fringe',
  'City: Mid-size',
  'City: Small',
  'Town: Remote',
  'Town: Distant',
  'Suburb: Small',
  'Rural: Remote',
  'Town: Fringe',
  'Suburb: Mid-size',
  '',
] as const

export type LocaleOption = (typeof LOCALE_OPTIONS)[number]

export const RELIGIOUS_AFFILIATION_OPTIONS = [
  'Other',
  'Roman Catholic',
  'Islamic',
  'Christian (unspecified)',
  'Baptist',
  'Church of God',
  'Pentecostal',
  'Presbyterian',
  'Episcopal',
  'Seventh-Day Adventist',
  'Jewish',
  'Assembly of God',
  'Church of Christ',
  'Calvinist',
  'Methodist',
  'Mennonite',
  'Friends',
  'Lutheran Church—Missouri Synod',
  'Church of God in Christ',
  'African Methodist Episcopal',
  'Church of the Nazarene',
  'Brethren',
  'Wisconsin Evangelical Lutheran Synod',
  'Other Lutheran',
  'Evangelical Lutheran Church in America',
  'Latter Day Saints',
  'Amish',
  'Greek Orthodox',
  'Nonsectarian',
  '',
] as const

export type ReligiousAffiliation = (typeof RELIGIOUS_AFFILIATION_OPTIONS)[number]

export type APIFilters = {
  schoolType?: SchoolType
  isActive?: boolean
  minSize?: number
  maxSize?: number
  startDate?: string
  endDate?: string
  virtual?: boolean
  collegeFair?: boolean
  title1Program?: Title1Program
  isTitle1Eligible?: boolean
  pctFreeReducedLunchMin?: number
  pctFreeReducedLunchMax?: number
  pctFirstGenerationStudentsMin?: number
  pctFirstGenerationStudentsMax?: number
  pctAttendTwoYearCollegeStudentsMin?: number
  pctAttendTwoYearCollegeStudentsMax?: number
  pctAttendFourYearCollegeStudentsMin?: number
  pctAttendFourYearCollegeStudentsMax?: number
  pctWhiteCaucasianStudentsMin?: number
  pctWhiteCaucasianStudentsMax?: number
  pctBlackAfricanAmericanStudentsMin?: number
  pctBlackAfricanAmericanStudentsMax?: number
  pctHispanicLatinoStudentsMin?: number
  pctHispanicLatinoStudentsMax?: number
  pctAsianStudentsMin?: number
  pctAsianStudentsMax?: number
  pctNativeAmericanStudentsMin?: number
  pctNativeAmericanStudentsMax?: number
  pctOtherUndisclosedStudentsMin?: number
  pctOtherUndisclosedStudentsMax?: number
  text?: string
  locations?: HSLocation[]
  religiousAffiliation?: ReligiousAffiliation[]
  locale?: LocaleOption[]
  cities?: string[]
  zipCodes?: string[]
  geomarkets?: string[]
  interestedHandraisersMin?: number
  interestedHandraisersMax?: number
  interestedHandraisersClasses?: Class[]
  interestedFollowersMin?: number
  interestedFollowersMax?: number
  interestedFollowersClasses?: Class[]
  historicFollowersMin?: number
  historicFollowersMax?: number
  historicFollowersYears?: HistoricFollowerYear[]
  hasCounselorSuggestions?: boolean
  isTarget?: boolean
}

export type PercentageRange = {
  min?: number
  max?: number
}

export type UIFilters = {
  religiousAffiliation?: ReligiousAffiliation[]
  schoolType?: SchoolType
  isActive?: true
  isTitle1Eligible?: boolean
  classSize?: PercentageRange
  pctFreeReducedLunch?: PercentageRange
  pctFirstGenerationStudents?: PercentageRange
  pctAttendTwoYearCollegeStudents?: PercentageRange
  pctAttendFourYearCollegeStudents?: PercentageRange
  pctWhiteCaucasianStudents?: PercentageRange
  pctBlackAfricanAmericanStudents?: PercentageRange
  pctHispanicLatinoStudents?: PercentageRange
  pctAsianStudents?: PercentageRange
  pctNativeAmericanStudents?: PercentageRange
  pctOtherUndisclosedStudents?: PercentageRange
  visitRange?: {
    type: 'this-week' | 'this-month' | 'next-month' | 'custom'
    startDate?: Date
    endDate?: Date
  }
  virtual?: boolean
  collegeFair?: boolean
  title1Program?: 'swp' | 'tas'
  text?: string
  locations?: HSLocation[]
  locale?: LocaleOption[]
  cities?: string[]
  zipCodes?: string[]
  geomarkets?: string[]
  interestedHandraisers?: {
    min?: number
    max?: number
  }
  interestedFollowers?: {
    min?: number
    max?: number
  }
  historicFollowers?: {
    min?: number
    max?: number
  }
  hasCounselorSuggestions?: boolean
  isTarget?: boolean
}

export const isValidSchoolType = (v?: string): v is SchoolType => v !== undefined && ['public', 'private'].includes(v)

export const API_DATE_FORMAT = 'yyyy-MM-dd'

export const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500] as const
export const isValidPageSizeOption = (v: number): v is PageSizeOption => PAGE_SIZE_OPTIONS.includes(v as PageSizeOption)
export type PageSizeOption = (typeof PAGE_SIZE_OPTIONS)[number]

export type SortKey = Exclude<
  keyof ExploreHighSchool | 'distance' | 'numHandraisers' | 'numFollowers' | 'numHistoricFollowers',
  'id'
>
const allowedSortKeys: SortKey[] = [
  'name',
  'target',
  'city',
  'schoolType',
  'seniorClassSize',
  'nextVisitDate',
  'state',
  'distance',
  'title1Program',
  'pctFreeReducedLunch',
  'pctFirstGenerationStudents',
  'pctAttendFourYearCollegeStudents',
  'pctAttendTwoYearCollegeStudents',
  'isActive',
  'isTitle1Eligible',
  'religiousAffiliation',
  'locale',
  'geomarket',
  'zipCode',
  'numHandraisers',
  'numFollowers',
  'numHistoricFollowers',
  'pctWhiteCaucasianStudents',
  'pctBlackAfricanAmericanStudents',
  'pctHispanicLatinoStudents',
  'pctAsianStudents',
  'pctNativeAmericanStudents',
  'pctOtherUndisclosedStudents',
  'hasCounselorSuggestions',
]

export const stringAsValidSortKey = (v?: string): SortKey | undefined => {
  if (isValidSortKey(v)) {
    return v
  }
}
export const isValidSortKey = (v?: string): v is SortKey => allowedSortKeys.includes(v as SortKey)

export const isValidTitle1Program = (v?: string): v is UIFilters['title1Program'] => {
  return v === 'swp' || v === 'tas'
}
const allowedSortDirections = ['asc', 'desc'] as const
export type SortDirection = (typeof allowedSortDirections)[number]

export const stringAsSortDirection = (v?: string): SortDirection | undefined => {
  return isValidSortDirection(v) ? v : undefined
}
const isValidSortDirection = (v?: string): v is SortDirection => allowedSortDirections.includes(v as SortDirection)

export interface ResultOptions {
  from?: number
  limit: number
  sortKey: SortKey
  sortDirection: SortDirection
}
